import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined'
import { colors } from '../../const/colors'
import { dateIso2localeString } from '../../utils/formatDate'
import {
  ICancellationEvent,
  cancelledOnReservationDay,
} from '../../utils/cancellationEvents'
import { CARD_SX, SectionHeader } from './UserDetailsUi'

const COLLAPSED_COUNT = 5

const CancelledReservationsSection: FC<{
  cancellations: ICancellationEvent[]
}> = ({ cancellations }) => {
  const { t, i18n } = useTranslation(['user_details', 'generic'])
  const [expanded, setExpanded] = useState(false)

  const visible = expanded
    ? cancellations
    : cancellations.slice(0, COLLAPSED_COUNT)

  return (
    <Box sx={CARD_SX}>
      <SectionHeader
        icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
        title={t('cancelled_reservations')}
        action={
          cancellations.length ? (
            <Box
              component="span"
              sx={{
                background: colors.redBg,
                color: colors.red,
                fontSize: '11.5px',
                fontWeight: 700,
                p: '4px 11px',
                borderRadius: colors.radiusTag,
              }}
            >
              {cancellations.length}
            </Box>
          ) : undefined
        }
      />

      {cancellations.length ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {visible.map(event => {
              const late = cancelledOnReservationDay(event)
              const period = `${dateIso2localeString(
                event.validFrom,
                i18n.language,
                {},
              )} – ${dateIso2localeString(event.validTo, i18n.language, {})}`
              return (
                <Box
                  key={event.key}
                  data-e2e="cancelled-reservation"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '13px',
                    p: '12px 14px',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: '140px' }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        width: 'fit-content',
                        background: colors.redBg,
                        color: colors.red,
                        fontSize: '13px',
                        fontWeight: 600,
                        p: '4px 10px',
                        borderRadius: colors.radiusTag,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {event.kind === 'subscription'
                        ? period
                        : dateIso2localeString(
                            event.day as string,
                            i18n.language,
                            {},
                          )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '11.5px',
                        color: colors.textMuted,
                        fontWeight: 600,
                        mt: '4px',
                      }}
                    >
                      {event.kind === 'subscription'
                        ? event.dayCount
                          ? t('cancelled_whole_with_days', {
                              n: event.dayCount,
                            })
                          : t('cancelled_whole')
                        : t('cancelled_from_reservation', { range: period })}
                    </Typography>
                    {!!event.parking && (
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          color: colors.textMuted,
                          fontWeight: 600,
                          mt: '2px',
                        }}
                      >
                        {event.parking}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: colors.navy,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {event.cancelledAt
                        ? t('cancelled_at_value', {
                            value: dateIso2localeString(
                              event.cancelledAt,
                              i18n.language,
                            ),
                          })
                        : t('cancelled_time_unknown')}
                    </Typography>
                    {!!event.cancelledBy && (
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          color: colors.textMuted,
                          fontWeight: 600,
                        }}
                      >
                        {t('cancelled_by_value', { value: event.cancelledBy })}
                      </Typography>
                    )}
                    {late && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          mt: '5px',
                          background: colors.redBg,
                          color: colors.red,
                          fontSize: '11px',
                          fontWeight: 700,
                          p: '3px 9px',
                          borderRadius: colors.radiusTag,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('cancelled_same_day')}
                      </Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>

          {cancellations.length > COLLAPSED_COUNT && (
            <Box
              onClick={() => setExpanded(prev => !prev)}
              sx={{
                mt: '10px',
                fontSize: '12.5px',
                fontWeight: 700,
                color: colors.textSecondary,
                cursor: 'pointer',
                '&:hover': { color: colors.navy },
              }}
            >
              {expanded
                ? t('cancelled_show_less')
                : t('cancelled_show_all', { n: cancellations.length })}
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: '13px',
            p: '14px 16px',
            fontSize: '13px',
            color: colors.slate,
            fontWeight: 600,
          }}
        >
          <EventBusyIcon sx={{ fontSize: 17 }} />
          {t('no_cancelled_reservations')}
        </Box>
      )}
    </Box>
  )
}

export default CancelledReservationsSection
